import type { PointData } from 'pixi.js';
import { getLocalizedValue } from '@softgames/localization';
import MathUtils from '../../../utils/MathUtils';
import { animateColorTo } from '../../../utils/pixi/animations/filter-animation-utils';
import type { Tween } from '../../../libs/TweenManager/Tween';
import type { PiggyBankProgressBarLayout } from '../../../libs/layouts/types/LayoutShapes';

// Using centralized PiggyBankProgressBarLayout

export class PiggyBankProgressBarView {
  private readonly maxBarWidth: number;

  private static readonly minBarWidth = 75;

  private displayedVales = {
    count: 0,
    maxCount: 0,
  };

  private readonly initialScale: PointData;

  private inAttractMode = false;

  private attractTweens: Tween[] = [];

  constructor(private readonly layout: PiggyBankProgressBarLayout) {
    this.maxBarWidth = layout.progressBar.width;

    layout.progressBar.width = 0;
    layout.progressBar.mask = layout.progressBarMask;

    this.initialScale = layout.progressBarContainer.scale.clone();
  }

  public reset(maxCount: number): void {
    void this.updateProgress(0, maxCount, 0);
  }

  public async updateProgress(count: number, maxCount: number, duration = 500): Promise<void> {
    const { displayedVales } = this;
    if (displayedVales.count === count && displayedVales.maxCount === maxCount) return;

    const { progressBar, progressText } = this.layout;

    const minBarProgress = this.maxBarWidth > 0 ? PiggyBankProgressBarView.minBarWidth / this.maxBarWidth : 0;
    const progress = MathUtils.clamp(count / maxCount, minBarProgress, 1);
    const barWidth = progress * this.maxBarWidth;

    progressBar.removeTweens();
    progressText.removeTweens();

    const formattedCount = getLocalizedValue(count);
    const formattedMax = getLocalizedValue(maxCount);

    if (duration === 0) {
      progressBar.width = barWidth;

      progressText.text = `${formattedCount}/${formattedMax}`;
    } else {
      await Promise.all([
        progressBar.tweenTo({ width: barWidth }, duration, 'quadOut', true).asyncOnComplete(),
        progressText
          .tweenNumericTextValue(
            {
              from: displayedVales.count,
              to: count,
              template: { text: `# / ${formattedMax}`, marker: '#' },
              roundMethod: Math.ceil,
            },
            duration,
            'quadOut',
            true,
          )
          .asyncOnComplete(),
      ]);

      if (count === maxCount) {
        progressText.text = formattedMax;
        void progressText.tweenTo({ scaleXY: 1.2 }, 100, 'sinIn', true).asyncOnComplete();
      }
    }

    this.displayedVales = {
      count,
      maxCount,
    };
  }

  public startAttractLoop(scaleMultiplier = 1.06, scaleDuration = 800): void {
    if (this.inAttractMode) return;
    this.inAttractMode = true;

    const { initialScale } = this;
    const { progressBarContainer } = this.layout;

    const attractTweens = [
      progressBarContainer.tweenTo(
        { scaleX: initialScale.x * scaleMultiplier, scaleY: initialScale.y * scaleMultiplier },
        scaleDuration,
        'quadOut',
        true,
        0,
        -1,
        true,
      ),
    ];

    this.attractTweens = attractTweens;
  }

  public flashBarBrightness(toAlpha = 0.8): void {
    animateColorTo(this.layout.progressBar, 0xffffff, 0, toAlpha, 250, 'quadOut', 0, 0, true);
  }

  public stopAttractLoop(scaleDuration = 200): void {
    if (!this.inAttractMode) return;
    this.inAttractMode = false;

    const { initialScale } = this;
    const { progressBarContainer } = this.layout;

    this.attractTweens.forEach((tween) => tween.stop());

    progressBarContainer.tweenTo({ scaleX: initialScale.x, scaleY: initialScale.y }, scaleDuration, 'quadOut', true);

    this.attractTweens = [];
  }

  public isFull(): boolean {
    return this.displayedVales.count === this.displayedVales.maxCount;
  }
}
